import React from 'react';
import ThemeSettings, { Theme } from '../../../../theme';
import { Box, BoxProps }  from '../Box/Box';
import classNames from 'classnames';
import styles from './ComputableColumn.css';
import { Helmet } from 'react-helmet-async';

export type ComputableColumnProps = {
    size: number | number[];
    base?: number;
    theme?: Theme;
} & BoxProps;

const sizeBlock = (width: number): string => {
    return `
        flex-basis: ${width}%;
        max-width: ${width}%;
    `;
};

type SizeFnResult = number | { desktop: number, mobile: number, phone: number };

function sizeFn(size: number | number[], base: number = 12): SizeFnResult {
    if (typeof size === 'number') {
        return Math.min(size as number, base) / base * 100;
    } else {
        const desktopWidth = Math.min(size[0] as number, base) / base * 100;
        const mobileWidth = Math.min(size[1] as number, base) / base * 100;
        const phoneWidth = size.length > 2 && Math.min(size[2] as number, base) / base * 100;

        return {
            desktop: desktopWidth,
            mobile: mobileWidth,
            ...phoneWidth && {phone: phoneWidth}
        };
    }
}

function getSelectorString(obj: any): string {
    const keys = Object.keys(obj);

    return keys.map(k => `[${k}="${obj[k]}"]`).join('');
}

function mapOptionsToAttributes(otps = {}) {
    const keys = Object.keys(otps);
    const reducer = (attrs, val) => ({
            [`data-${val}`]: otps[val],
            ...attrs
    });

    return keys.reduce(reducer, {});
}

function sizeStyle(size: SizeFnResult, selector: string, theme: Theme): string {
    if (typeof size === 'number') {
        return `
            .${selector} {
                ${sizeBlock(size)}
            }
        `;
    } else {
        return `
            @media(min-width: ${theme.breakPoints.desktop}px) {
                .${selector} {
                    ${sizeBlock(size.desktop)}
                }
            }

            @media(max-width: ${theme.breakPoints.mobile}px) {
                .${selector} {
                    ${sizeBlock(size.mobile)}
                }
            }

            ${size.phone && `
                @media(max-width: ${theme.breakPoints.phone}px) {
                    .${selector} {
                        ${sizeBlock(size.phone)}
                    }
                }`
            }
        `;
    }
}

export const ComputableColumn = React.memo(({ className, ...props}: ComputableColumnProps) => {
    let attributes;
    const size = sizeFn(props.size, props.base);

    if (typeof props.size === 'number') {
        attributes = { 'data-size': `${size}%` };
    } else {
        attributes = mapOptionsToAttributes(size);
    }

    return (
        <React.Fragment>
            <Helmet>
                <style key = {getSelectorString(attributes)}>
                    {
                        sizeStyle(
                            size,
                            `${styles.base}${getSelectorString(attributes)}`,
                            ThemeSettings
                        )
                    }
                </style>
            </Helmet>
            <Box
                className = {classNames(styles.base, className)}
                {...props}
                {...attributes}
            />
        </React.Fragment>
    );
});

ComputableColumn.displayName = 'ComputableColumn';
